<template>
  <div
    class="
      sanitas-health-forecast-questionnaire
      sanitas-health-forecast-questionnaire-container
    "
    :class="{ 'corporate-context': isCorporate }"
  >
    <div
      class="sanitas-health-forecast-questionnaire-wrapper"
      :style="{ minHeight: minHeightValue + 'px' }"
    >
      <div
        class="sanitas-health-forecast-questionnaire-content"
        :style="{ minHeight: minHeightContent + 'px' }"
      >
        <div class="local-question">
          <p class="question">{{ localQuestion.text }}</p>
        </div>
        <div v-if="!hasAnswered" class="answer-content">
          <sanitas-button
            v-for="(option, i) in answers"
            ref="answerButton"
            :key="i"
            type="button"
            class="answer-button"
            @sanitas_button_tap="answerSelect(i, true)"
          >
            {{ option }}
          </sanitas-button>
        </div>
        <div
          v-if="hasAnswered && !isWaiting"
          class="chart"
          :class="
            doughnutChart
              ? 'doughnut-width'
              : segmentsBarChart
              ? 'segments-chart-width'
              : 'chart-width'
          "
        >
          <div v-if="doughnutChart" ref="chartDoughnut" class="chart-doughnut">
            <doughnut-chart
              id="doughnutChart"
              ref="doughnutChart"
              class="chart-doughnut-canvas"
              :chart-data="chartDoughnutData"
              :chart-options="doughnutChartOptions"
              :plugins="plugins"
            ></doughnut-chart>
            <div class="chart-legend-doughnut">
              <div
                v-for="(label, i) in chartDoughnutData.labels"
                :key="i"
                class="doughnut-legend"
              >
                <p
                  class="doughnut-legend-box"
                  :style="{
                    'background-color': chartDoughnutData.backgroundColor[i],
                  }"
                ></p>
                <p
                  class="doughnut-legend-label"
                  :style="{ 'font-weight': i === answerIndex ? 'bold' : '' }"
                >
                  {{ label }}
                </p>
              </div>
            </div>
          </div>
          <div v-if="barChart" class="chart-bar">
            <bar-chart
              id="barChart"
              ref="barChart"
              :height="minHeightValueBarChart"
              :chart-data="chartBarData"
              :chart-options="barChartOptions"
              :plugins="plugins"
            ></bar-chart>
          </div>
          <div v-if="segmentsBarChart" class="chart-bar-segments">
            <bar-chart
              id="segmentsBarChart"
              :chart-data="segmentsBarChartData"
              :chart-options="segmentsBarChartOptions"
              :plugins="plugins"
            ></bar-chart>
            <div
              v-for="(dataset, i) in segmentsBarChartData.datasets"
              :key="i"
              class="segment-legend"
            >
              <p
                class="segment-legend-box"
                :style="{ 'background-color': dataset.backgroundColor }"
              ></p>
              <p class="segment-legend-label">
                {{ dataset.label }}
              </p>
            </div>
          </div>
          <div
            v-if="simpleNumber"
            class="simple-number"
            v-html="localQuestion.result"
          >
            {{ localQuestion.result }}
          </div>
        </div>
        <div
          v-if="hasAnswered && !isWaiting && !simpleNumber"
          class="result"
          v-html="localQuestion.result"
        >
          {{ localQuestion.result }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import {
  ArcElement,
  BarController,
  BarElement,
  CategoryScale,
  Chart,
  DoughnutController,
  Legend,
  LinearScale,
  Tooltip,
} from 'chart.js'
import ChartDataLabels from 'chartjs-plugin-datalabels'
import isEmpty from 'lodash/isEmpty'
import isString from 'lodash/isString'
import stringHash from 'string-hash'

import SanitasButton from '@/components/sanitas-components/SanitasButton'
import { setTrackerVersion, trackAction, tracker } from '@/helpers/tracker'

import BarChart from './charts/Bar'
import DoughnutChart from './charts/Doughnut'

Chart.register(
  ArcElement,
  BarElement,
  BarController,
  CategoryScale,
  DoughnutController,
  LinearScale,
  Legend,
  Tooltip,
)

Chart.register(ChartDataLabels)

export default {
  name: 'HealthForecastQuestionnaire',

  components: {
    BarChart,
    DoughnutChart,
    SanitasButton,
  },

  props: {
    questionKey: {
      type: String,
      default: '',
    },
    question: {
      type: [Object, String],
      default: () => ({}),
    },
    context: {
      type: String,
      default: '',
    },
  },

  data() {
    return {
      localQuestion: {},
      localStorageKey: undefined,
      minHeightValue: 210,
      minHeightValueBarChart: 0,
      minHeightContent: 210,
      hasAnswered: false,
      isWaiting: false,
      answerIndex: 0,
      backgroundColors: ['#f6b830', '#5bac26', '#004435', '#bb2d2f', '#99cde3'],
      barchartColor: '#99cde3',
      barchartHighlightColor: '#b48476',
      colorIndex: 0,
      chartDoughnutData: {
        labels: [],
        backgroundColor: [],
        datasets: [
          {
            data: [],
            backgroundColor: [],
            borderColor: ['#f3f2f0'],
            borderWidth: 0,
          },
        ],
      },
      doughnutChartOptions: {
        plugins: {
          datalabels: {
            color: '#ffffff',
            formatter: value => {
              let newValue = this.setPercentageValues(
                this.absoluteValues,
                value,
              )
              return newValue
            },
          },
          legend: {
            display: false,
          },
          tooltip: {
            callbacks: {
              title: tooltipItems => tooltipItems[0].label,
              label: tooltipItem => {
                const dataIndex = tooltipItem.dataIndex
                const label = tooltipItem.dataset.data[dataIndex]
                const { absoluteValues } = tooltipItem.chart.config.data
                return this.setPercentageValues(absoluteValues, label)
              },
            },
          },
        },
        cutout: '65%',
        maintainAspectRatio: false,
        responsive: true,
      },
      chartBarData: {
        labels: [],
        datasets: [
          {
            data: [],
            backgroundColor: context => {
              let index = context.dataIndex
              return index === parseInt(this.answerIndex)
                ? this.barchartHighlightColor
                : this.barchartColor
            },
            borderWidth: 0,
            barThickness: 25,
          },
        ],
      },
      barChartOptions: {
        indexAxis: 'y',
        plugins: {
          datalabels: {
            display: false,
          },
          legend: {
            display: false,
          },
          tooltip: {
            callbacks: {
              title: tooltipItems => tooltipItems[0].label.split(' ')[0],
              label: tooltipItem => {
                const dataIndex = tooltipItem.dataIndex
                const label = tooltipItem.dataset.data[dataIndex]
                const { absoluteValues } = tooltipItem.chart.config.data
                return this.setPercentageValues(absoluteValues, label)
              },
            },
          },
        },
        maintainAspectRatio: false,
        responsive: true,
        display: false,
        title: {
          display: false,
        },
        scales: {
          x: {
            beginAtZero: true,
            ticks: {
              display: false,
            },
            grid: {
              display: false,
              drawBorder: false,
            },
          },
          y: {
            ticks: {
              display: true,
              mirror: true,
              fontSize: 15,
              padding: 0,
              labelOffset: -20,
            },
            grid: {
              display: false,
              drawBorder: false,
            },
          },
        },
        ticks: {
          font: context => {
            const fontOptions = {}
            if (context.tick?.major) {
              fontOptions.weight = '700'
            }
            return fontOptions
          },
        },
        layout: {
          padding: {
            left: 24,
            right: 26,
            top: 0,
            bottom: 0,
          },
        },
      },
      segmentsBarChartData: {
        labels: [],
        datasets: [],
      },
      plugins: [
        {
          beforeDraw: (chart, _args, _options) => {
            if (isEmpty(chart.scales)) {
              return
            }
            this.setMajorTick(chart, this.answerIndex)
          },
          afterInit: (chart, _args, _options) => {
            let minHeight = 0
            if (chart.canvas.id.includes('doughnut-chart')) {
              minHeight = this.$refs.chartDoughnut.scrollHeight - 20
              this.minHeightValue = minHeight
              this.minHeightContent = minHeight - 15
            }
          },
        },
      ],
      segmentsBarChartOptions: {
        indexAxis: 'y',
        plugins: {
          datalabels: {
            display: true,
            clamp: true,
            color: '#191919',
            fontSize: '15px',
            anchor: 'end',
            align: 'end',
            formatter: value => {
              let newValue = this.setPercentageValues(
                this.absoluteValues,
                value,
              )
              return newValue
            },
          },
          legend: {
            display: false,
          },
          tooltip: {
            mode: 'nearest',
            callbacks: {
              title: tooltipItems => tooltipItems[0].dataset.label,
              label: tooltipItem =>
                this.setPercentageValues(this.absoluteValues, tooltipItem.raw),
            },
          },
        },
        layout: {
          padding: {
            left: 4,
            right: 42,
            top: 0,
            bottom: 0,
          },
        },
        maintainAspectRatio: false,
        responsive: true,

        title: {
          display: true,
          text: '',
        },
        ticks: {
          font: context => {
            const fontOptions = {}
            if (context.tick?.major) {
              fontOptions.weight = '700'
            }
            return fontOptions
          },
        },
        datasets: {
          x: {
            barPercentage: 0.9,
            categoryPercentage: 0.55,
          },
        },
        scales: {
          x: {
            beginAtZero: true,
            grid: {
              display: false,
              drawBorder: false,
            },
            ticks: {
              display: false,
            },
          },
          y: {
            display: true,
            padding: 0,
            offset: true,
            ticks: {
              mirror: true,
              labelOffset: -64,
            },
            major: {
              enabled: true,
            },
            grid: {
              display: false,
              drawBorder: false,
            },
          },
        },
      },
    }
  },

  computed: {
    doughnutChart() {
      return this.localQuestion.diagramType === 'Donut chart'
    },
    barChart() {
      return this.localQuestion.diagramType === 'Bar chart'
    },
    segmentsBarChart() {
      return this.localQuestion.diagramType === 'Bar chart with segments'
    },
    simpleNumber() {
      return this.localQuestion.diagramType === 'Simple Number'
    },
    absoluteValues() {
      return this.localQuestion.absoluteValues === true
    },
    simpleNumberHeight() {
      return 225
    },
    answers() {
      return this.getAnswers()
    },
    groupedOptions() {
      return this.groupData()
    },
    actionPath() {
      return this.localQuestion.actionPath
    },
    calculatePercentages() {
      return this.localQuestion.calculatePercentages === true
    },
    isCorporate() {
      return this.context === 'corporate'
    },
  },

  created() {
    Chart.defaults.font.family = "'Open Sans', sans-serif"
    Chart.defaults.font.size = 15
    Chart.defaults.color = '#191919'

    if (this.isCorporate) {
      this.backgroundColors = [
        '#f78e1e',
        '#00765A',
        '#5BAC26',
        '#F6B830',
        '#FFE539',
      ]
      this.barchartColor = '#00765a'
      this.barchartHighlightColor = '#5bac26'
    }
  },

  mounted() {
    setTrackerVersion()

    this.localQuestion = isString(this.question)
      ? JSON.parse(this.question)
      : this.question
    if (this.questionKey) {
      this.localStorageKey = 'questionnaire-' + this.questionKey
    } else {
      let hashValue = this.localQuestion.text + JSON.stringify(this.answers)
      this.localStorageKey =
        'questionnaire-' + hashValue.substring(0, 8) + stringHash(hashValue)
    }
    if (parseInt(localStorage[this.localStorageKey]) >= 0) {
      this.answerSelect(parseInt(localStorage[this.localStorageKey]), false)
    }
  },

  methods: {
    setMajorTick(chart, answerIndex) {
      const ticks = chart.scales.y.ticks
      if (ticks.length >= answerIndex) {
        ticks[answerIndex].$context.tick.major = true
      }
    },
    setMinHeight(value) {
      this.minHeightValue = 290 + value
    },
    setMinContentHeight(value) {
      this.minHeightContent = this.minHeightValue - 20
    },
    answerSelect(index, newSelection) {
      this.hasAnswered = true
      if (this.actionPath) {
        let originalIndex = index
        this.isWaiting = true
        let promise
        if (newSelection) {
          const formData = new FormData()
          formData.append('answer', this.answers[index])
          formData.append(':formid', 'form')
          formData.append(':formstart', 'undefined')
          formData.append('_charset_', 'UTF-8')
          promise = axios.post(this.actionPath, formData)
        } else {
          promise = axios.get(this.actionPath)
        }
        promise
          .then(response => {
            if (response && response.data && response.data.length) {
              if (this.calculatePercentages) {
                let total = 0
                response.data.forEach(el => {
                  total += parseInt(el.value)
                })
                response.data.forEach(el => {
                  el.value = Math.round((parseInt(el.value) / total) * 100)
                })
              }
              response.data.forEach(el => {
                if (el.name) {
                  this.localQuestion.options.forEach(el2 => {
                    if (el2.label === el.name) {
                      el2.value = el.value
                    }
                  })
                }
              })
              let newOptions = []
              this.localQuestion.options.forEach((el, elIndex) => {
                if (elIndex == originalIndex) {
                  if (el.value == 0) {
                    el.value = 1
                  }
                  if (isNaN(el.value)) {
                    el.value = 100
                  }
                }
                if (el.value > 0) {
                  newOptions.push(el)
                  if (elIndex == originalIndex) {
                    index = newOptions.length - 1
                  }
                }
              })
              if (newOptions.length) {
                this.localQuestion.options = newOptions
              }
            }
            this.isWaiting = false
            this.getData()
            this.answerIndex = index
            if (newSelection) {
              localStorage[this.localStorageKey] = originalIndex

              tracker.set({
                data: {
                  qa: {
                    question: this.localQuestion.text,
                    answer: this.answers[index],
                  },
                },
              })

              trackAction('ComHFQuestionnaireAnswered')
            }
          })
          .catch(err => {
            console.error(err)
          })
      } else {
        this.getData()
        this.answerIndex = index
        if (newSelection) {
          localStorage[this.localStorageKey] = index

          tracker.set({
            data: {
              qa: {
                question: this.localQuestion.text,
                answer: this.answers[index],
              },
            },
          })

          trackAction('ComHFQuestionnaireAnswered')
        }
      }
    },
    getData() {
      if (this.localQuestion.options) {
        if (this.doughnutChart) {
          this.colorIndex = 0
          let chartData = this.setChartData(this.chartDoughnutData)
          if (chartData.datasets[0].data.length) {
            this.setChartDatasetBackgroundColors(chartData)
          }
          this.chartDoughnutData = Object.assign({}, this.chartDoughnutData)
        } else if (this.barChart) {
          this.setChartData(this.chartBarData, true)
          this.chartBarData = Object.assign({}, this.chartBarData)
          let numberOfQuestions = this.chartBarData.datasets[0].data.length
          if (numberOfQuestions < 4) {
            this.minHeightValue = 120 + numberOfQuestions * 20
          } else if (numberOfQuestions < 7) {
            this.minHeightValue = 120 + numberOfQuestions * 35
          } else {
            this.setMinHeight(numberOfQuestions * 20)
          }
          this.minHeightValueBarChart = this.minHeightValue
          this.setMinContentHeight()
        } else if (this.segmentsBarChart) {
          this.colorIndex = 0
          this.setSegmentedChartLabels(this.segmentsBarChartData)
          this.setSegmentedChartDatasetData(this.segmentsBarChartData)
          this.segmentsBarChartData.absoluteValues = this.absoluteValues
          let numberOfDatasets = this.segmentsBarChartData.datasets.length
          this.setMinHeight(
            numberOfDatasets > 3
              ? numberOfDatasets * 50
              : numberOfDatasets * 55,
          )
          this.setMinContentHeight()
        } else if (this.simpleNumber) {
          this.minHeightValue = this.simpleNumberHeight
          this.minHeightContent = this.simpleNumberHeight
        }
      }
    },
    setChartData(chartData, labelWithValues = false) {
      chartData.labels = []
      chartData.datasets[0].data = []
      chartData.absoluteValues = this.absoluteValues
      this.localQuestion.options.forEach((item, i) => {
        let newValue = this.setPercentageValues(
          chartData.absoluteValues,
          item.value,
        )
        let itemLabel = labelWithValues
          ? item.label + ' ' + newValue
          : item.label
        chartData.labels[i] = itemLabel
        chartData.datasets[0].data[i] = item.value
      })
      return chartData
    },
    setChartDatasetBackgroundColors(chartData) {
      chartData.datasets[0].backgroundColor = []
      chartData.datasets[0].data.forEach((item, i) => {
        chartData.backgroundColor[i] = this.backgroundColors[this.colorIndex]
        chartData.datasets[0].backgroundColor[i] =
          this.backgroundColors[this.colorIndex]
        this.colorIndex =
          this.colorIndex === this.backgroundColors.length - 1
            ? 0
            : this.colorIndex + 1
      })
      return chartData
    },
    setSegmentedChartLabels(chartData) {
      chartData.labels = []
      this.answers.forEach(segmentTitle => {
        chartData.labels.push(segmentTitle)
      })
    },
    setSegmentedChartDatasetData(chartData) {
      return this.groupedOptions.forEach((item, i) => {
        let dataValues = []
        let dataset = this.emptyDataset(this.colorIndex, i)
        this.colorIndex =
          this.colorIndex === this.backgroundColors.length - 1
            ? 0
            : this.colorIndex + 1
        dataset.label = item[0]
        item[1].forEach(subItem => {
          dataValues.push(subItem.value)
        })
        dataset.data = dataValues
        chartData.datasets.push(dataset)
      })
    },
    emptyDataset(colorIndex, i) {
      return {
        data: [],
        label: '',
        backgroundColor: this.backgroundColors[colorIndex],
        borderWidth: 0,
        barPercentage: 0.8,
        categoryPercentage: 0.6,
      }
    },
    groupData() {
      let arr = this.localQuestion.options
      let prop = 'segment'
      const map = new Map(Array.from(arr, obj => [obj[prop], []]))
      arr.forEach(obj => map.get(obj[prop]).push(obj))
      return Array.from(map)
    },
    getAnswers() {
      let answers = []
      if (this.localQuestion.options) {
        this.localQuestion.options.forEach(item => {
          if (!this.segmentsBarChart || !answers.includes(item.label)) {
            answers.push(item.label)
          }
        })
      }
      return answers
    },
    setPercentageValues(absoluteValues, value) {
      return absoluteValues ? value : value + '%'
    },
  },
}
</script>

<style lang="postcss" scoped>
.sanitas-health-forecast-questionnaire {
  text-decoration: none;

  &-wrapper {
    background-color: var(--c-primary-neutral-2);
    display: flex;
    flex-direction: column;
    align-items: stretch;
    flex: 1;
  }

  &-container {
    transition: 0.3s;
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    align-items: stretch;
    margin-bottom: 40px;
  }

  &-content {
    border-top: none;
    color: var(--c-primary-neutral-1);
    position: relative;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    .question {
      width: 66%;
      max-width: 685px;
      font-family: Faustina, serif;
      font-size: 40px;
      font-weight: bold;
      line-height: 40px;
      color: var(--c-primary-neutral-1);
      margin-bottom: 80px;
      .corporate-context & {
        font-size: 30px;
        line-height: 35px;
      }
    }
    .result {
      font-size: 25px;
      color: var(--c-primary-neutral-1);
      width: 66%;
      max-width: 685px;
    }
    @media (max-width: 860px) {
      .question {
        margin-bottom: 10px;
      }
    }
    .chart {
      position: absolute;
      min-width: calc(100% - 685px);
      height: 100%;
      top: 0;
      right: 0;
      .chart-doughnut {
        margin-left: auto;
      }
      .simple-number /deep/ p,
      span,
      h1,
      h2,
      h3,
      h4,
      h5,
      h6 {
        font-weight: bold;
        color: var(--c-primary-color-3);
        padding-top: 25px;
        padding-left: 42px;
        padding-right: 42px;
        font-size: 15px;
        font-family: 'Faustina', serif !important;
        /deep/ b {
          font-size: 140px;
          line-height: 140px;
          display: block;
        }
        b:after {
          content: '\A';
          white-space: pre;
        }
      }
      .segment-legend {
        display: flex;
        align-items: center;
        margin: 0 0 10px 14px;
      }
      .segment-legend-box,
      .doughnut-legend-box {
        width: 10px;
        height: 10px;
        margin: 0 8px 0 0;
        flex-shrink: 0;
      }
      .segment-legend-label,
      .doughnut-legend-label {
        font-size: 15px;
        line-height: 15px;
        margin: 0;
      }
      .chart-legend-doughnut {
        display: flex;
        flex-wrap: wrap;
      }
      .doughnut-legend {
        float: left;
        min-width: 50%;
        display: inline-flex;
        align-items: center;
        min-height: 30px;
      }

      @media (min-width: 861px) {
        .chart-doughnut {
          padding-left: 35px;
        }
        .chart-bar-segments,
        .simple-number {
          padding-left: 20px;
        }
      }

      @media (max-width: 860px) {
        padding-left: 0px;
        .segment-legend {
          margin: 0 0 10px 4px;
        }
        .chart-legend-doughnut {
          margin-top: 15px;
          margin-bottom: 20px;
        }
        .doughnut-legend {
          float: left;
          width: 20%;
          display: flex;
          align-items: center;
        }
        .chart-bar {
          padding-bottom: 20px;
        }
        .chart-bar #barChart /deep/ canvas {
          margin-left: -34px;
        }
        .chart-bar-segments {
          margin-bottom: 20px;
        }
        .chart-bar-segments #segmentsBarChart {
          /deep/ canvas {
            margin-left: -10px;
          }
        }
      }
      @media (max-width: 440px) {
        .doughnut-legend {
          float: left;
          width: 100%;
          display: flex;
          align-items: center;
          margin-left: 0;
        }
      }
    }
    .answer-button {
      border-radius: 0;
      margin: 20px 10px 0 0 !important;
      border: solid 1px var(--c-primary-neutral-1);
      background-color: var(--c-primary-neutral-2);
      color: var(--c-primary-neutral-1);
      height: auto;
      min-height: 40px;
      min-width: 160px;
      padding: 5px !important;
      font-size: 15px;
      font-weight: normal;
      letter-spacing: 0.485px;
      white-space: normal;
    }
    .answer-button:hover {
      background-color: var(--c-primary-color-3);
      color: var(--c-primary-neutral-3);
      font-size: 15px;
      font-weight: bold;
      border: none;
      cursor: pointer;
      white-space: normal;
    }
    .doughnut-width {
      width: 37%;
    }
    @media (min-width: 861px) {
      .doughnut-width {
        top: -30px;
      }
      .segments-chart-width {
        width: 40%;
        right: -25px;
        top: -40px;
      }
      .chart-width {
        width: 40%;
        right: -25px;
      }
    }
    @media (max-width: 860px) {
      .chart-width {
        top: 25px;
      }
      .answer-content {
        margin-top: 40px;
      }
      .question,
      .result {
        width: 100%;
        max-width: initial;
      }
      .chart {
        width: 100%;
        position: relative;
        .chart-doughnut {
          margin: 15px 0 0 0;
          max-height: initial;
        }
        .chart-doughnut-canvas {
          max-width: 300px;
        }
      }
      .simple-number /deep/ p,
      span,
      h1,
      h2,
      h3,
      h4,
      h5,
      h6 {
        padding-top: 15px;
        padding-bottom: 25px;
        padding-left: initial;
        width: 100%;
        font-size: 15px;
        line-height: 20px;
        b {
          line-height: 140px;
          font-size: 140px;
          display: block;
        }
      }
    }
    @media (max-width: 400px) {
      .answer-button {
        width: 100%;
      }
    }
  }

  @media (min-width: 861px) {
    &-wrapper {
      padding: 40px;
    }
  }

  @media (max-width: 860px) {
    &-wrapper {
      min-height: 280px;
      padding: 20px;
    }
  }
}

@media (max-width: 490px) {
  .sanitas-health-forecast-questionnaire {
    width: 100%;
    padding: 6px 0;
    &-container {
      height: auto;
    }
  }
}

.magazine-article .content-area .sanitas-health-forecast-questionnaire {
  &-content {
    .result /deep/ p {
      font-size: 25px;
      line-height: 30px;
      margin-bottom: 0px;
    }
    .chart {
      .simple-number /deep/ p,
      span,
      h1,
      h2,
      h3,
      h4,
      h5,
      h6 {
        padding-left: 0px;
        padding-right: 5px;
        margin-bottom: 0px;
        line-height: 20px;
        b {
          font-size: 140px;
          line-height: 140px;
          display: block;
        }
        b:after {
          content: '\A';
          white-space: pre;
        }
      }
      @media (max-width: 860px) {
        .simple-number /deep/ p,
        span,
        h1,
        h2,
        h3,
        h4,
        h5,
        h6 {
          padding-top: 10px;
        }
      }
      .segment-legend-box /deep/,
      .segment-legend-label /deep/,
      .doughnut-legend-box /deep/,
      .doughnut-legend-label /deep/ {
        margin-bottom: 0px !important;
      }
    }
  }
}
</style>
